// src/components/dashboard/SideNavbar.js
import React, { useContext, useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Logo from '../shared/Logo';
import { AuthContext } from '../../context/AuthContext';
import ChatInterface from '../chat/ChatInterface';
import NotificationDropdown from '../NotificationCenter/NotificationDropdown';
import { useNotifications } from '../../hooks/useNotifications';
import '../../styles/SideNavbar.scss';
import { useLocation } from 'react-router-dom';
import { getEffectiveCountry } from '../../utils/environmentConfig';

// Import react-icons for compatibility
import { FaUserCircle, FaSignOutAlt, FaHome, FaListAlt, FaComments, FaCog, FaAngleDown, FaAngleUp, FaCommentDots, FaBell, FaTasks, FaFolder, FaUsers, FaBook, FaGraduationCap, FaQuestionCircle, FaFileAlt, FaStickyNote, FaEdit, FaClipboardList, FaLayerGroup, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

function SideNavbar({ onResetApp }) {
  const { userData, clearToken, authToken } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [isNotificationDropdownOpen, setIsNotificationDropdownOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    // Initialize from localStorage
    const saved = localStorage.getItem('sideNavbarCollapsed');
    return saved === 'true';
  });
  const dropdownRef = useRef(null);
  const notificationRef = useRef(null);
  const username = userData?.username;
  const isAdmin = username === "charles" || username === "cillian";
  
  // Use the notifications hook
  const { unreadCount } = useNotifications();

  // Toggle collapsed state and save to localStorage
  const toggleCollapsed = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    localStorage.setItem('sideNavbarCollapsed', newState.toString());
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setIsNotificationDropdownOpen(false);
      }
    };

    if (isDropdownOpen || isNotificationDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen, isNotificationDropdownOpen]);

  const handleLogout = () => {
    setIsDropdownOpen(false);
    clearToken(true);
    navigate('/');
  };

  const handleHomeClick = () => {
    // Call the reset function if provided to clear state
    if (typeof onResetApp === 'function') {
      onResetApp();
    }
    
    navigate('/');
    setIsDropdownOpen(false);
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const toggleNotificationDropdown = () => {
    setIsNotificationDropdownOpen(!isNotificationDropdownOpen);
  };

  const handleResolvePayment = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/create-portal-session`,
        {
          method: 'POST',
          headers: {
            'Authorization': authToken?.startsWith('Bearer ') ? authToken : `Bearer ${authToken}`,
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create portal session');
      }

      const data = await response.json();
      window.location.href = data.url;
    } catch (err) {
      // Keep UX simple here; subscription page has richer error handling
      alert('Could not open billing portal. Please try again.');
    } finally {
      setIsDropdownOpen(false);
    }
  };

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      <nav className={`side-navbar ${isCollapsed ? 'collapsed' : ''}`}>
        <div className="side-navbar-content">
          {/* Logo Section */}
          <div className="side-navbar-header">
            <button
              onClick={handleHomeClick}
              className="side-navbar-logo-link"
            >
              <Logo className="side-navbar-logo" />
            </button>
          </div>

          {/* Toggle Button */}
          <button
            onClick={toggleCollapsed}
            className="side-navbar-toggle"
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? <FaChevronRight /> : <FaChevronLeft />}
          </button>

          {/* Navigation Items */}
          <div className="side-navbar-nav">
            {userData?.username ? (
              <>
                {/* Home */}
                <button
                  onClick={handleHomeClick}
                  className={`side-navbar-item ${isActive('/') && location.pathname !== '/study-plans' ? 'active' : ''}`}
                  title="Home"
                >
                  <FaHome />
                  <span className="side-navbar-label">Home</span>
                </button>

                {/* My Submissions / Student Submissions */}
                {userData?.role === 'teacher' ? (
                  <>
                    <Link
                      to="/student-submissions"
                      className={`side-navbar-item ${isActive('/student-submissions') ? 'active' : ''}`}
                      title="Student Submissions"
                    >
                      <FaUsers />
                      <span className="side-navbar-label">Student Submissions</span>
                    </Link>
                    <Link
                      to="/my-submissions"
                      className={`side-navbar-item ${isActive('/my-submissions') ? 'active' : ''}`}
                      title="My Submissions"
                    >
                      <FaFolder />
                      <span className="side-navbar-label">My Submissions</span>
                    </Link>
                  </>
                ) : (
                  <Link
                    to="/dashboard"
                    className={`side-navbar-item ${isActive('/dashboard') ? 'active' : ''}`}
                    title="My Submissions"
                  >
                    <FaListAlt />
                    <span className="side-navbar-label">My Submissions</span>
                  </Link>
                )}

                {/* Mark Essay / Mark my essay */}
                {userData?.role === 'teacher' ? (
                  <Link
                    to="/essay/subject"
                    className={`side-navbar-item ${isActive('/essay') ? 'active' : ''}`}
                    title="Mark Essay"
                  >
                    <FaEdit />
                    <span className="side-navbar-label">Mark Essay</span>
                  </Link>
                ) : userData?.role === 'student' ? (
                  <Link
                    to="/essay/subject"
                    className={`side-navbar-item ${isActive('/essay') ? 'active' : ''}`}
                    title="Mark My Essay"
                  >
                    <FaEdit />
                    <span className="side-navbar-label">Mark My Essay</span>
                  </Link>
                ) : null}

                {/* Quizzes */}
                <Link
                  to="/quiz"
                  className={`side-navbar-item ${isActive('/quiz') ? 'active' : ''}`}
                  title={userData?.role === 'teacher' ? 'Our Quizzes' : 'Quizzes'}
                >
                  <FaClipboardList />
                  <span className="side-navbar-label">{userData?.role === 'teacher' ? 'Our Quizzes' : 'Quizzes'}</span>
                </Link>

                {/* Flashcards - students only */}
                {userData?.role === 'student' && (
                  <Link
                    to="/flashcards"
                    className={`side-navbar-item ${isActive('/flashcards') ? 'active' : ''}`}
                    title="Study Flashcards"
                  >
                    <FaLayerGroup />
                    <span className="side-navbar-label">Flashcards</span>
                  </Link>
                )}

                {/* My Notes / Create Quiz */}
                <Link
                  to="/notes"
                  className={`side-navbar-item ${isActive('/notes') ? 'active' : ''}`}
                  title={userData?.role === 'teacher' ? 'Create Quiz' : 'My Notes'}
                >
                  <FaStickyNote />
                  <span className="side-navbar-label">{userData?.role === 'teacher' ? 'Create Quiz' : 'My Notes'}</span>
                </Link>

                {/* Study Plans - students only */}
                {userData?.role === 'student' && (
                  <Link
                    to="/study-plans"
                    className={`side-navbar-item ${isActive('/study-plans') ? 'active' : ''}`}
                    title="Study Timetable"
                  >
                    <FaGraduationCap />
                    <span className="side-navbar-label">Study Timetable</span>
                  </Link>
                )}

                {/* My Classrooms - teachers only */}
                {userData?.role === 'teacher' && (
                  <Link
                    to="/classroom-management"
                    className={`side-navbar-item ${isActive('/classroom-management') ? 'active' : ''}`}
                    title="My Classrooms"
                  >
                    <FaUsers />
                    <span className="side-navbar-label">My Classrooms</span>
                  </Link>
                )}

                {/* Exam Papers - Ireland only */}
                {getEffectiveCountry(userData?.country) === 'Ireland' && (
                  <Link
                    to="/exam-papers/subject"
                    className={`side-navbar-item ${isActive('/exam-papers') ? 'active' : ''}`}
                    title="Exam Papers"
                  >
                    <FaFileAlt />
                    <span className="side-navbar-label">Exam Papers</span>
                  </Link>
                )}

                {/* Submit to Teacher - for students only */}
                {userData?.role === 'student' && (
                  <Link
                    to="/my-classrooms"
                    className={`side-navbar-item ${isActive('/my-classrooms') ? 'active' : ''}`}
                    title="Submit to Teacher"
                  >
                    <FaUsers />
                    <span className="side-navbar-label">Submit to Teacher</span>
                  </Link>
                )}

                {/* Chat button - only show for students */}
                {location.pathname !== '/study-plans' && userData?.role === 'student' && (
                  <button
                    onClick={() => setShowChat(true)}
                    className={`side-navbar-item ${isActive('/chat') ? 'active' : ''}`}
                    title="Start New Chat"
                  >
                    <FaCommentDots />
                    <span className="side-navbar-label">Chat</span>
                  </button>
                )}

                {/* Feedback */}
                <Link
                  to="/feedback"
                  className={`side-navbar-item ${isActive('/feedback') ? 'active' : ''}`}
                  title="Give Feedback"
                >
                  <FaComments />
                  <span className="side-navbar-label">Feedback</span>
                </Link>

                {/* Help */}
                <Link
                  to="/help"
                  className={`side-navbar-item ${isActive('/help') ? 'active' : ''}`}
                  title="Help"
                >
                  <FaQuestionCircle />
                  <span className="side-navbar-label">Help</span>
                </Link>

                {/* My Account - for all users */}
                <Link
                  to="/account"
                  className={`side-navbar-item ${isActive('/account') ? 'active' : ''}`}
                  title="My Account"
                >
                  <FaUserCircle />
                  <span className="side-navbar-label">My Account</span>
                </Link>

                {/* Notification Bell with Dropdown */}
                <div className="side-navbar-notification-menu" ref={notificationRef}>
                  <button
                    onClick={toggleNotificationDropdown}
                    className={`side-navbar-item ${isNotificationDropdownOpen ? 'active' : ''}`}
                    title="Notifications"
                  >
                    <div className="side-navbar-notification-wrapper">
                      <FaBell />
                      {unreadCount > 0 && (
                        <span className="side-navbar-notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
                      )}
                    </div>
                    <span className="side-navbar-label">Notifications</span>
                  </button>

                  <NotificationDropdown 
                    isOpen={isNotificationDropdownOpen}
                    onClose={() => setIsNotificationDropdownOpen(false)}
                  />
                </div>
              </>
            ) : (
              // Logged out state
              <div className="side-navbar-auth">
                <Link to="/login" className="side-navbar-item">
                  Login
                </Link>
                <Link to="/register" className="side-navbar-item side-navbar-item-primary">
                  Sign Up
                </Link>
              </div>
            )}
          </div>

          {/* User Menu at Bottom */}
          {userData?.username && (
            <div className="side-navbar-footer">
              <div className="side-navbar-user-menu" ref={dropdownRef}>
                <button
                  onClick={toggleDropdown}
                  className={`side-navbar-user-button ${isDropdownOpen ? 'active' : ''}`}
                  title="Account Menu"
                >
                  <div className="side-navbar-user-avatar">
                    {username?.charAt(0).toUpperCase()}
                  </div>
                  <div className="side-navbar-user-info">
                    <span className="side-navbar-username">{username}</span>
                    <span className="side-navbar-user-role">{userData?.role || 'Student'}</span>
                  </div>
                  {isDropdownOpen ? <FaAngleUp /> : <FaAngleDown />}
                </button>

                {isDropdownOpen && (
                  <div className="side-navbar-dropdown-content">
                    {userData?.role !== 'teacher' && (() => {
                      const status = userData?.subscription_status;
                      if (status === 'active' || status === 'trialing') {
                        return (
                          <Link 
                            to="/subscribe" 
                            onClick={() => setIsDropdownOpen(false)}
                            className="side-navbar-dropdown-link"
                          >
                            ⚙️ Manage Subscription
                          </Link>
                        );
                      }
                      if (status === 'past_due') {
                        return (
                          <button
                            onClick={handleResolvePayment}
                            className="side-navbar-dropdown-button side-navbar-dropdown-button-warning"
                          >
                            ⚠️ Resolve payment
                          </button>
                        );
                      }
                      return (
                        <Link 
                          to="/subscribe" 
                          onClick={() => setIsDropdownOpen(false)}
                          className="side-navbar-dropdown-link side-navbar-dropdown-link-primary"
                        >
                          ⭐ Upgrade to Premium
                        </Link>
                      );
                    })()}
                    
                    {isAdmin && (
                      <>
                        <div className="side-navbar-dropdown-divider"></div>
                        <Link to="/admin/feedback" onClick={() => setIsDropdownOpen(false)}>
                          <FaCog /> View Feedback
                        </Link>
                        <Link to="/admin/essays" onClick={() => setIsDropdownOpen(false)}>
                          <FaCog /> View Essays
                        </Link>
                      </>
                    )}
                    
                    <div className="side-navbar-dropdown-divider"></div>
                    <Link to="/faq" onClick={() => setIsDropdownOpen(false)}>
                      FAQ
                    </Link>
                    <div className="side-navbar-dropdown-divider"></div>
                    <button onClick={handleLogout} className="side-navbar-dropdown-button side-navbar-dropdown-button-danger">
                      <FaSignOutAlt /> Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Chat Interface - only for students */}
      {showChat && (
        <ChatInterface
          show={showChat}
          onClose={() => setShowChat(false)}
          forceNewConversation={true}
          authToken={authToken}
          API_BASE_URL={process.env.REACT_APP_API_BASE_URL || require('../../utils/environmentConfig').getApiBaseUrl()}
        />
      )}
    </>
  );
}

export default SideNavbar;

